import {useState, useCallback} from "react";
import Box from "@mui/material/Box";

/**
 * TabbedWrapper — reusable uncontrolled tab component with gold bar and
 * "file tab" materialisation effect.
 *
 * Props:
 *   tabs        — [{ key: string, label: string, disabled?: boolean }]
 *   defaultTab  — string: initial active tab key (defaults to first tab's key)
 *   onTabChange — (newTab, prevTab) => void — called AFTER the switch
 *   children    — JSX rendered inside the content panel
 */
export const TabbedWrapper = ({tabs = [], defaultTab, activeTab: controlledTab, onTabChange, bordered = false, children}) => {
    const [internalTab, setInternalTab] = useState(defaultTab || tabs[0]?.key || "");
    const isControlled = controlledTab !== undefined;
    const activeTab = isControlled ? controlledTab : internalTab;

    const handleTabClick = useCallback((tabKey) => {
        if (tabKey === activeTab) return;
        const prevTab = activeTab;
        if (!isControlled) setInternalTab(tabKey);
        onTabChange?.(tabKey, prevTab);
    }, [activeTab, isControlled, onTabChange]);

    const borderRadius = 8;

    return (
        <Box sx={{
            width: "100%",
            ...(bordered ? {border: "1px solid #FFFFFF", borderRadius: `${borderRadius}px`} : {}),
        }}>
            {/* Gold bar with tabs */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    backgroundColor: "#FFC540",
                    borderRadius: bordered
                        ? `${borderRadius - 1}px ${borderRadius - 1}px 0 0`
                        : `${borderRadius}px ${borderRadius}px 0 0`,
                    minHeight: 38,
                    px: 0.5,
                    pt: 0.5,
                }}
            >
                {tabs.map((tab) => {
                    const isActive = tab.key === activeTab;
                    const isDisabled = tab.disabled === true;

                    return (
                        <Box
                            key={tab.key}
                            onClick={isDisabled ? undefined : () => handleTabClick(tab.key)}
                            sx={{
                                px: 2.5,
                                py: 1,
                                pb: 1.5,
                                cursor: isDisabled ? "default" : "pointer",
                                userSelect: "none",
                                fontFamily: "Poppins, sans-serif",
                                fontSize: "14px",
                                fontWeight: 600,
                                transition: "all 0.15s ease",
                                position: "relative",

                                ...(isActive
                                    ? {
                                        backgroundColor: "#3A3E3F",
                                        color: "#FFC540",
                                        borderRadius: `${borderRadius}px ${borderRadius}px 0 0`,
                                        "&::after": {
                                            content: '""',
                                            position: "absolute",
                                            bottom: 4,
                                            left: "50%",
                                            transform: "translateX(-50%)",
                                            width: "60%",
                                            height: "2px",
                                            backgroundColor: "#FFC540",
                                            borderRadius: "2px",
                                        },
                                    }
                                    : {
                                        backgroundColor: "transparent",
                                        color: isDisabled ? "rgba(62, 58, 63, 0.4)" : "#5F5F5F",
                                        borderRadius: 0,
                                        "&:hover": isDisabled
                                            ? {}
                                            : {color: "#3E3A3F"},
                                    }),
                            }}
                        >
                            {tab.label}
                        </Box>
                    );
                })}
            </Box>

            {/* Content panel */}
            <Box
                sx={{
                    backgroundColor: "#3A3E3F",
                    borderRadius: `0 0 ${borderRadius}px ${borderRadius}px`,
                    p: 3,
                    minHeight: 200,
                }}
            >
                {children}
            </Box>
        </Box>
    );
};
